import React, { useEffect, useRef, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import './LineChart.css';

ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, Tooltip);

function formatChartValue(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return Number.isInteger(numericValue)
    ? String(numericValue)
    : numericValue.toFixed(2).replace(/\.?0+$/, '');
}

function estimateLabelWidth(text, fontSize = 12) {
  return Math.max(fontSize, String(text || '').length * fontSize * 0.62);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getClosestPointIndex(points, targetX) {
  if (!Array.isArray(points) || points.length === 0) {
    return -1;
  }

  let closestIndex = 0;
  let closestDistance = Math.abs(points[0].x - targetX);

  for (let index = 1; index < points.length; index += 1) {
    const distance = Math.abs(points[index].x - targetX);
    if (distance < closestDistance) {
      closestDistance = distance;
      closestIndex = index;
    }
  }

  return closestIndex;
}

function buildPath(points) {
  if (!Array.isArray(points) || points.length === 0) {
    return '';
  }

  return points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');
}

function normalizeSeries(dataPoints, dates) {
  const safeDates = Array.isArray(dates) ? dates : [];
  const safeValues = Array.isArray(dataPoints) ? dataPoints : [];
  const count = Math.min(safeDates.length, safeValues.length);

  const labels = [];
  const values = [];

  for (let index = 0; index < count; index += 1) {
    labels.push(String(safeDates[index] || ''));
    const numericValue = Number(safeValues[index]);
    values.push(Number.isFinite(numericValue) ? numericValue : 0);
  }

  return { labels, values };
}

function MobileLineChart({ dataPoints, dates, title }) {
  const viewportRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const { labels, values } = normalizeSeries(dataPoints, dates);

  const chartHeight = 180;
  const padding = {
    left: 28,
    right: 16,
    top: 16,
    bottom: 28,
  };
  const pointCount = values.length;
  const hasManyPoints = pointCount > 6;
  const pointSpacing = 56;
  const baseViewportWidth = Math.max(viewportWidth, 280);
  const chartWidth =
    pointCount > 1
      ? Math.max(
          baseViewportWidth,
          padding.left + padding.right + (pointCount - 1) * pointSpacing
        )
      : baseViewportWidth;
  const drawableHeight = chartHeight - padding.top - padding.bottom;
  const drawableWidth = Math.max(1, chartWidth - padding.left - padding.right);
  const baselineY = chartHeight - padding.bottom;
  const maxValue = Math.max(...values, 0);
  const safeMaxValue = maxValue > 0 ? maxValue : 1;
  const points = values.map((value, index) => ({
    value,
    x:
      pointCount <= 1
        ? padding.left + drawableWidth / 2
        : padding.left + (drawableWidth * index) / (pointCount - 1),
    y: padding.top + (1 - value / safeMaxValue) * drawableHeight,
  }));
  const pathData = buildPath(
    points.length > 0
      ? [
          { x: Math.max(0, padding.left - 10), y: baselineY },
          ...points,
        ]
      : []
  );
  const centerX = scrollLeft + baseViewportWidth / 2;
  const centeredIndex = getClosestPointIndex(points, centerX);
  const activeIndex =
    selectedIndex !== null && selectedIndex >= 0 && selectedIndex < pointCount
      ? selectedIndex
      : centeredIndex;
  const activePoint = activeIndex >= 0 ? points[activeIndex] : null;
  const activeDate = activeIndex >= 0 ? labels[activeIndex] : '';
  const activeValueText = activePoint ? formatChartValue(activePoint.value) : '';
  const visiblePointX = activePoint ? activePoint.x - scrollLeft : 0;
  const dateLabelWidth = estimateLabelWidth(activeDate, 12);
  const valueLabelLeft = 10;
  const valueLabelWidth = estimateLabelWidth(activeValueText, 12);
  const guideGapAfterValue = 8;
  const dateRightMargin = activeIndex === pointCount - 1 ? 28 : 10;
  const dateShiftLeft = activeIndex === pointCount - 1 ? 14 : 0;
  const horizontalGuideStartX = activePoint
    ? Math.min(
        activePoint.x - 8,
        Math.max(
          padding.left,
          scrollLeft + valueLabelLeft + valueLabelWidth + guideGapAfterValue
        )
      )
    : padding.left;
  const dateLabelLeft = clamp(
    visiblePointX - dateLabelWidth / 2 - dateShiftLeft,
    10,
    Math.max(10, baseViewportWidth - dateLabelWidth - dateRightMargin)
  );
  const valueLabelTop = activePoint
    ? clamp(activePoint.y - 14, padding.top + 2, chartHeight - padding.bottom - 12)
    : padding.top;
  const dateLabelTop = chartHeight - padding.bottom - 6;

  useEffect(() => {
    const element = viewportRef.current;
    if (!element) {
      return undefined;
    }

    const syncViewportWidth = () => {
      const nextWidth = element.clientWidth || 0;
      setViewportWidth(nextWidth);
      setScrollLeft(element.scrollLeft || 0);
    };

    syncViewportWidth();

    if (typeof ResizeObserver === 'function') {
      const resizeObserver = new ResizeObserver(() => {
        syncViewportWidth();
      });
      resizeObserver.observe(element);
      return () => resizeObserver.disconnect();
    }

    window.addEventListener('resize', syncViewportWidth);
    return () => window.removeEventListener('resize', syncViewportWidth);
  }, []);

  useEffect(() => {
    if (selectedIndex === null) {
      return;
    }

    if (selectedIndex >= pointCount) {
      setSelectedIndex(pointCount > 0 ? pointCount - 1 : null);
    }
  }, [pointCount, selectedIndex]);

  const handleScroll = (event) => {
    setScrollLeft(event.currentTarget.scrollLeft || 0);
    setSelectedIndex(null);
  };

  const handleChartSelect = (event) => {
    if (!viewportRef.current || points.length === 0) {
      return;
    }

    const svgRect = event.currentTarget.getBoundingClientRect();
    const targetX = event.clientX - svgRect.left;
    const nextIndex = getClosestPointIndex(points, targetX);
    if (nextIndex >= 0) {
      setSelectedIndex(nextIndex);
    }
  };

  return (
    <div className="progress-chart-container progress-chart-container--mobile">
      <div className="chart-title">{title}</div>
      <div className="mobile-chart-stage">
        <div
          className={`mobile-chart-scroll${hasManyPoints ? ' mobile-chart-scroll--wide' : ''}`}
          ref={viewportRef}
          onScroll={handleScroll}
        >
          <svg
            className="mobile-chart-svg"
            width={chartWidth}
            height={chartHeight}
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={title}
            onClick={handleChartSelect}
          >
            {activePoint ? (
              <>
                <line
                  className="mobile-chart-guide"
                  x1={activePoint.x}
                  y1={activePoint.y}
                  x2={activePoint.x}
                  y2={chartHeight - padding.bottom}
                />
                <line
                  className="mobile-chart-guide"
                  x1={horizontalGuideStartX}
                  y1={activePoint.y}
                  x2={activePoint.x}
                  y2={activePoint.y}
                />
              </>
            ) : null}
            {pathData ? <path className="mobile-chart-path" d={pathData} /> : null}
            {/* One marker per game. The line's starting point at zero is only
                there to draw from, so it gets none. */}
            {points.map((point, index) => (
              <circle
                key={index}
                className={`mobile-chart-point${index === activeIndex ? ' mobile-chart-point--active' : ''}`}
                cx={point.x}
                cy={point.y}
                r={index === activeIndex ? 5 : 3.5}
              />
            ))}
          </svg>
        </div>
        {activePoint ? (
          <div className="mobile-chart-overlay">
            <div
              className="mobile-chart-label mobile-chart-label--value"
              style={{
                left: `${valueLabelLeft}px`,
                top: `${valueLabelTop}px`,
              }}
            >
              {activeValueText}
            </div>
            <div
              className="mobile-chart-label mobile-chart-label--date"
              style={{
                left: `${dateLabelLeft}px`,
                top: `${dateLabelTop}px`,
              }}
            >
              {activeDate}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const LineChart = ({ dataPoints, dates, title }) => {
  const [isSmallScreen, setIsSmallScreen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(max-width: 768px)');
    const syncScreenSize = (event) => {
      setIsSmallScreen(event.matches);
    };

    setIsSmallScreen(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', syncScreenSize);
      return () => mediaQuery.removeEventListener('change', syncScreenSize);
    }

    mediaQuery.addListener(syncScreenSize);
    return () => mediaQuery.removeListener(syncScreenSize);
  }, []);

  if (isSmallScreen) {
    return <MobileLineChart dataPoints={dataPoints} dates={dates} title={title} />;
  }

  const crosshairPlugin = {
    id: 'crosshairPlugin',
    afterEvent(chart, args) {
      const event = args.event;

      if (event.type === 'mousemove') {
        const points = chart.getElementsAtEventForMode(
          event.native,
          'nearest',
          { intersect: false },
          true
        );

        if (points.length) {
          const firstPoint = points[0];
          const datasetIndex = firstPoint.datasetIndex;
          const dataIndex = firstPoint.index;

          const x = chart.scales.x.getPixelForValue(dataIndex);
          const yValue = chart.data.datasets[datasetIndex].data[dataIndex];
          const y = chart.scales.y.getPixelForValue(yValue);

          chart.crosshair = {
            x,
            y,
            xValue: chart.data.labels[dataIndex],
            yValue,
          };
        } else {
          chart.crosshair = null;
        }
      } else if (event.type === 'mouseout') {
        chart.crosshair = null;
      }
    },
    afterDraw(chart) {
      if (!chart.crosshair) {
        return;
      }

      const { x, y, xValue, yValue } = chart.crosshair;
      const {
        ctx,
        chartArea: { left, top, bottom },
      } = chart;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, bottom);
      ctx.moveTo(x, y);
      ctx.lineTo(left, y);
      ctx.strokeStyle = 'rgba(30, 182, 45, 1)';
      ctx.stroke();

      ctx.fillStyle = '#1EB62D';
      ctx.font = '14px Nyxerin';

      ctx.textAlign = 'center';
      ctx.fillText(xValue, x, bottom + 15);

      const yLabelText = formatChartValue(yValue);
      const yLabelWidth = ctx.measureText(yLabelText).width;
      ctx.textAlign = 'left';
      ctx.fillText(
        yLabelText,
        left - yLabelWidth - 5,
        Math.max(top + 12, y + 3)
      );

      ctx.restore();
    },
  };

  const data = {
    labels: dates,
    datasets: [
      {
        label: title,
        data: dataPoints,
        borderColor: '#1EB62D',
        backgroundColor: 'transparent',
        borderWidth: 3,
        // One marker per game, ringed in the page navy so it stands off the line.
        pointRadius: 4.5,
        pointHoverRadius: 6.5,
        pointBackgroundColor: '#1EB62D',
        pointBorderColor: '#000d1c',
        pointBorderWidth: 2,
        pointHoverBackgroundColor: '#1EB62D',
        pointHoverBorderColor: '#000d1c',
        pointHoverBorderWidth: 2,
        tension: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        left: 100,
        right: 100,
        top: 30,
        bottom: 30,
      },
    },
    hover: {
      mode: 'nearest',
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        display: false,
        type: 'category',
        labels: dates,
      },
      y: {
        beginAtZero: true,
        display: false,
      },
    },
  };

  return (
    <div className="progress-chart-container">
      <div className="chart-title">{title}</div>
      <Line data={data} options={options} plugins={[crosshairPlugin]} />
    </div>
  );
};

export default LineChart;
